import { useState } from "react";
import axios from "axios";
import Reveal from "./Reveal";
import { whatsappLink } from "../config";
import { toast } from "sonner";
import { MessageCircle, Mail, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const etapas = ["Embarazo", "Parto", "Puerperio", "Menopausia", "Otro"];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", etapa: "", message: "" });
  const [sending, setSending] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Por favor completa nombre, email y mensaje.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("¡Mensaje enviado! Te responderé muy pronto. 💜");
      setForm({ name: "", email: "", etapa: "", message: "" });
    } catch (err) {
      toast.error("No se pudo enviar el mensaje. Inténtalo de nuevo o escríbeme por WhatsApp.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-32 bg-crema">
      <div className="mx-auto max-w-6xl px-6 md:px-12 grid lg:grid-cols-2 gap-14 lg:gap-20 items-start">
        <Reveal>
          <p className="font-sans text-xs tracking-[0.35em] uppercase text-terracota mb-5">Contacto</p>
          <h2 className="font-serif font-light text-4xl md:text-5xl text-ciruela leading-tight tracking-tight">
            Empecemos a <span className="italic text-terracota">caminar juntas</span>
          </h2>
          <p className="font-body text-lg text-ciruela-dark/75 mt-6 leading-relaxed">
            Cuéntame en qué momento estás y cómo puedo acompañarte. La forma más rápida de hablar conmigo
            es por WhatsApp.
          </p>

          <div className="mt-10 space-y-4">
            <a
              href={whatsappLink()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp-btn"
              className="flex items-center gap-4 rounded-2xl bg-terracota text-crema px-6 py-5 hover:bg-terracota-dark transition-colors duration-300"
            >
              <MessageCircle className="h-7 w-7" />
              <div>
                <p className="font-sans font-semibold">Escríbeme por WhatsApp</p>
                <p className="font-body text-sm text-crema/85">Respuesta cercana y rápida</p>
              </div>
            </a>
            <a
              href="mailto:hola@lorenacotano.com"
              data-testid="contact-email-btn"
              className="flex items-center gap-4 rounded-2xl bg-ambar/40 border border-rosa/30 text-ciruela px-6 py-5 hover:bg-ambar/60 transition-colors duration-300"
            >
              <Mail className="h-6 w-6" />
              <div>
                <p className="font-sans font-semibold">hola@lorenacotano.com</p>
                <p className="font-body text-sm text-ciruela-dark/70">Escríbeme un correo</p>
              </div>
            </a>
          </div>
        </Reveal>

        <Reveal delay={120}>
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="rounded-[32px] bg-gradient-to-br from-ambar/30 to-rosa/15 border border-rosa/30 p-8 md:p-10 space-y-5"
          >
            <p className="font-serif italic text-2xl text-ciruela">O déjame un mensaje</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <input
                data-testid="contact-input-name"
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                placeholder="Tu nombre"
                className="w-full rounded-xl border border-rosa/40 bg-crema px-4 py-3 font-body text-ciruela-dark placeholder:text-ciruela-dark/40 focus:outline-none focus:border-terracota"
              />
              <input
                data-testid="contact-input-email"
                type="email"
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                placeholder="Tu email"
                className="w-full rounded-xl border border-rosa/40 bg-crema px-4 py-3 font-body text-ciruela-dark placeholder:text-ciruela-dark/40 focus:outline-none focus:border-terracota"
              />
            </div>
            <select
              data-testid="contact-input-etapa"
              value={form.etapa}
              onChange={(e) => update("etapa", e.target.value)}
              className="w-full rounded-xl border border-rosa/40 bg-crema px-4 py-3 font-body text-ciruela-dark focus:outline-none focus:border-terracota"
            >
              <option value="">¿En qué etapa estás? (opcional)</option>
              {etapas.map((et) => (
                <option key={et} value={et}>{et}</option>
              ))}
            </select>
            <textarea
              data-testid="contact-input-message"
              value={form.message}
              onChange={(e) => update("message", e.target.value)}
              placeholder="Cuéntame cómo puedo acompañarte..."
              rows={4}
              className="w-full rounded-xl border border-rosa/40 bg-crema px-4 py-3 font-body text-ciruela-dark placeholder:text-ciruela-dark/40 focus:outline-none focus:border-terracota resize-none"
            />
            <button
              type="submit"
              disabled={sending}
              data-testid="contact-submit-btn"
              className="w-full flex items-center justify-center gap-2 font-sans font-medium rounded-full bg-ciruela text-crema px-8 py-4 hover:bg-ciruela-dark transition-colors duration-300 disabled:opacity-60"
            >
              <Send className="h-4 w-4" />
              {sending ? "Enviando..." : "Enviar mensaje"}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
